import { useMutation } from "@tanstack/react-query";
import { TAlertMessages, useAlerts } from "context/AlertContext";
import {
  IAssetContent,
  IUpdateStructuredDocumentContentRequestData,
} from "models/assets.models";
import { TAssetId } from "models/documents.models";
// eslint-disable-next-line max-len
import useHandleStructuredDocumentVersionChange from "screens/Project/screens/StructuredDocument/hooks/useHandleStructuredDocumentVersionChange";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";

export type TUpdateStructuredDocumentContentMutation = {
  assetId: TAssetId;
  data: IUpdateStructuredDocumentContentRequestData;
};

export default function useUpdateStructuredDocumentContent(
  alertMessages?: Partial<TAlertMessages>,
) {
  const { showAlert } = useAlerts();

  const handleStructuredDocumentVersionChange =
    useHandleStructuredDocumentVersionChange();

  const { error, isPending, isError, isSuccess, mutateAsync } = useMutation<
    IAssetContent,
    Error,
    TUpdateStructuredDocumentContentMutation
  >({
    mutationFn: async ({ assetId, data }) => {
      try {
        const res = await services.assets.updateStructuredDocumentContent(
          assetId,
          data,
        );

        alertMessages?.successMessage &&
          showAlert({
            severity: AlertSeverity.Success,
            message: alertMessages.successMessage,
          });

        await handleStructuredDocumentVersionChange(
          assetId,
          res.data.assetId,
          res.data.versions[0].version,
        );

        return res.data;
      } catch (error) {
        alertMessages?.failureMessage &&
          showAlert({
            severity: AlertSeverity.Error,
            message: alertMessages.failureMessage,
          });

        throw error;
      }
    },
  });

  return {
    error,
    isPending,
    isError,
    isSuccess,
    updateStructuredDocumentContent: mutateAsync,
  };
}
